import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "manager-account" }
const _hoisted_2 = { class: "page-head" }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = {
  key: 1,
  class: "op-none"
}
const _hoisted_5 = { class: "drawer-footer" }

import { onMounted, reactive, ref, computed } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { ElMessage } from "element-plus";
import store from "@/store";
import {
  getManagerListApi,
  createManagerApi,
  toggleManagerApi,
  deleteManagerApi,
  type ManagerAccount,
} from "@/api/manager/managerAccountApi";

/** 当前登录的管理端账号 ID（其所在行不提供启停操作） */

export default /*@__PURE__*/_defineComponent({
  __name: 'ManagerAccountView',
  setup(__props) {

// 管理端账号管理：manager 表 CRUD（列表 + 添加 + 启用禁用）
const currentManagerId = computed<string>(
  () => store.getters["auth/managerInfo"]?.id ?? ""
);

/** 是否超级管理员（level=1）：账号启停/删除仅超管可见/可操作 */
const isSuperAdmin = computed<boolean>(
  () => store.getters["auth/managerInfo"]?.level === "1"
);

const loading = ref(false);
const managerList = ref<ManagerAccount[]>([]);

const fetchList = async () => {
  loading.value = true;
  const res = await getManagerListApi();
  if (res) managerList.value = res;
  loading.value = false;
};

onMounted(fetchList);

// ===== 启用/禁用 =====
const onToggle = async (row: ManagerAccount) => {
  const ok = await toggleManagerApi(row.id);
  if (ok) {
    ElMessage.success(row.isDeleted === 1 ? "已启用" : "已禁用");
    fetchList();
  } else {
    ElMessage.error("操作失败");
  }
};

// ===== 删除（物理删除，超管专属） =====
const onDelete = async (row: ManagerAccount) => {
  const ok = await deleteManagerApi(row.id);
  if (ok) {
    ElMessage.success("账号已删除");
    fetchList();
  } else {
    ElMessage.error("删除失败");
  }
};

// ===== 添加账号 =====
const drawer = ref({ visible: false, loading: false });
const formRef = ref<FormInstance>();
const form = reactive({
  name: "",
  username: "",
  password: "",
  confirmPassword: "",
  email: "",
});

const validateConfirm = (
  _: unknown,
  value: string,
  cb: (err?: Error) => void
) => {
  if (!value) return cb(new Error("请再次输入密码"));
  if (value !== form.password) return cb(new Error("两次输入密码不一致"));
  cb();
};

const rules: FormRules = {
  name: [{ required: true, message: "请输入姓名", trigger: "blur" }],
  username: [
    { required: true, message: "请输入用户名", trigger: "blur" },
    {
      pattern: /^[A-Za-z0-9]{5,20}$/,
      message: "用户名仅能包含数字和大小写字母，长度为5~20位",
      trigger: "blur",
    },
  ],
  password: [
    { required: true, message: "请输入密码", trigger: "blur" },
    {
      pattern: /^[0-9A-Za-z!?_.,#@]{8,20}$/,
      message: "密码需 8~20 位，仅可包含数字/字母/! ? _ . , # @",
      trigger: "blur",
    },
  ],
  confirmPassword: [
    { validator: validateConfirm, trigger: ["blur", "change"] },
  ],
  email: [
    { required: true, message: "请输入邮箱", trigger: "blur" },
    { type: "email", message: "邮箱格式不正确", trigger: "blur" },
  ],
};

const openCreateDrawer = () => {
  resetForm();
  drawer.value.visible = true;
};

const resetForm = () => {
  formRef.value?.clearValidate();
  form.name = "";
  form.username = "";
  form.password = "";
  form.confirmPassword = "";
  form.email = "";
};

const onDrawerBeforeClose = (done: () => void) => {
  if (drawer.value.loading) return;
  done();
};

const onCancel = () => {
  if (drawer.value.loading) return;
  drawer.value.visible = false;
};

const onSubmit = async () => {
  if (!formRef.value) return;
  await formRef.value.validate(async (valid) => {
    if (!valid) return;
    drawer.value.loading = true;
    try {
      const ok = await createManagerApi({
        name: form.name,
        username: form.username,
        password: form.password,
        email: form.email,
      });
      if (ok) {
        ElMessage.success("账号创建成功，可立即登录管理端");
        drawer.value.visible = false;
        fetchList();
      } else {
        ElMessage.error("创建失败");
      }
    } catch {
      ElMessage.error("创建失败");
    } finally {
      drawer.value.loading = false;
    }
  });
};

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_popconfirm = _resolveComponent("el-popconfirm")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_alert = _resolveComponent("el-alert")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_drawer = _resolveComponent("el-drawer")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[7] || (_cache[7] = _createElementVNode("div", null, [
        _createElementVNode("h1", { class: "page-title" }, "账号管理"),
        _createElementVNode("p", { class: "page-desc" }, " 管理端登录账号（开发公司内部员工）；与\"开通集团管理员账号\"（交付客户的用户端账号）是两套独立体系 ")
      ], -1)),
      _createVNode(_component_el_button, {
        type: "primary",
        onClick: openCreateDrawer
      }, {
        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
          _createTextVNode("添加账号", -1)
        ]))]),
        _: 1
      })
    ]),
    _withDirectives((_openBlock(), _createBlock(_component_el_table, {
      data: managerList.value,
      border: "",
      style: {"width":"100%"}
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_table_column, {
          fixed: "",
          type: "index",
          label: "ID",
          "min-width": "50"
        }),
        _createVNode(_component_el_table_column, {
          prop: "name",
          label: "姓名",
          "min-width": "140"
        }),
        _createVNode(_component_el_table_column, {
          prop: "username",
          label: "用户名",
          "min-width": "160"
        }),
        _createVNode(_component_el_table_column, {
          prop: "email",
          label: "邮箱",
          "min-width": "200"
        }),
        _createVNode(_component_el_table_column, {
          label: "状态",
          "min-width": "100"
        }, {
          default: _withCtx((scope) => [
            _createVNode(_component_el_tag, {
              type: scope.row.isDeleted === 1 ? 'danger' : 'success',
              size: "small"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(scope.row.isDeleted === 1 ? "已禁用" : "启用中"), 1)
              ]),
              _: 2
            }, 1032, ["type"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_table_column, {
          label: "身份",
          "min-width": "110"
        }, {
          default: _withCtx((scope) => [
            (scope.row.level === '1')
              ? (_openBlock(), _createBlock(_component_el_tag, {
                  key: 0,
                  type: "warning",
                  size: "small"
                }, {
                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createTextVNode(" 超级管理员 ", -1)
                  ]))]),
                  _: 1
                }))
              : (_openBlock(), _createElementBlock("span", _hoisted_3, "普通用户"))
          ]),
          _: 1
        }),
        _createVNode(_component_el_table_column, {
          label: "创建时间",
          "min-width": "180"
        }, {
          default: _withCtx((scope) => [
            _createTextVNode(_toDisplayString(scope.row.createtime
              ? String(scope.row.createtime).replace("T", " ").slice(0, 19)
              : "—"), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_el_table_column, {
          label: "操作",
          "min-width": "120",
          fixed: "right"
        }, {
          default: _withCtx((scope) => [
            (scope.row.id === currentManagerId.value)
              ? (_openBlock(), _createBlock(_component_el_tag, {
                  key: 0,
                  size: "small",
                  type: "info"
                }, {
                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                    _createTextVNode(" 当前账号 ", -1)
                  ]))]),
                  _: 1
                }))
              : (!isSuperAdmin.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, "—"))
                : (_openBlock(), _createBlock(_component_el_popconfirm, {
                    key: 2,
                    title: 
              scope.row.isDeleted === 1
                ? '确认启用该账号？'
                : '确认禁用该账号？（禁用后无法登录）'
            ,
                    onConfirm: ($event: any) => (onToggle(scope.row))
                  }, {
                    reference: _withCtx(() => [
                      _createVNode(_component_el_button, {
                        type: scope.row.isDeleted === 1 ? 'success' : 'danger',
                        link: "",
                        size: "small"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(scope.row.isDeleted === 1 ? "启用" : "禁用"), 1)
                        ]),
                        _: 2
                      }, 1032, ["type"])
                    ]),
                    _: 2
                  }, 1032, ["title", "onConfirm"])),
            (isSuperAdmin.value && scope.row.level !== '1')
              ? (_openBlock(), _createBlock(_component_el_popconfirm, {
                  key: 3,
                  title: "确认删除该账号？（物理删除，不可恢复！）",
                  "confirm-button-text": "删除",
                  "cancel-button-text": "取消",
                  onConfirm: ($event: any) => (onDelete(scope.row))
                }, {
                  reference: _withCtx(() => [
                    _createVNode(_component_el_button, {
                      type: "danger",
                      link: "",
                      size: "small"
                    }, {
                      default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                        _createTextVNode(" 删除 ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["onConfirm"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["data"])), [
      [_directive_loading, loading.value]
    ]),
    _createVNode(_component_el_drawer, {
      modelValue: drawer.value.visible,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((drawer.value.visible) = $event)),
      title: "添加管理端账号",
      size: "420px",
      "close-on-click-modal": !drawer.value.loading,
      "close-on-press-escape": !drawer.value.loading,
      "before-close": onDrawerBeforeClose,
      direction: "rtl",
      "append-to-body": ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_alert, {
          type: "info",
          closable: false,
          "show-icon": "",
          style: {"margin-bottom":"16px"}
        }, {
          title: _withCtx(() => [...(_cache[11] || (_cache[11] = [
            _createTextVNode(" 此处创建的是", -1),
            _createElementVNode("b", null, "管理端登录账号", -1),
            _createTextVNode("（开发公司内部使用），创建后即可登录本管理端。 ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_component_el_form, {
          ref_key: "formRef",
          ref: formRef,
          model: form,
          rules: rules,
          "label-width": "90px",
          "status-icon": ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "姓名",
              prop: "name"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.name,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.name) = $event)),
                  disabled: drawer.value.loading,
                  placeholder: "请输入姓名"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "用户名",
              prop: "username"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.username,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.username) = $event)),
                  disabled: drawer.value.loading,
                  placeholder: "5-20位，字母数字"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "密码",
              prop: "password"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.password,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.password) = $event)),
                  type: "password",
                  "show-password": "",
                  disabled: drawer.value.loading,
                  placeholder: "8-20位，允许常见符号"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "确认密码",
              prop: "confirmPassword"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.confirmPassword,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.confirmPassword) = $event)),
                  type: "password",
                  "show-password": "",
                  disabled: drawer.value.loading,
                  placeholder: "再次输入密码"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "邮箱",
              prop: "email"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.email,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.email) = $event)),
                  disabled: drawer.value.loading,
                  placeholder: "用于密码重置通知"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_el_button, {
                type: "success",
                loading: drawer.value.loading,
                onClick: onSubmit
              }, {
                default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                  _createTextVNode("创建", -1)
                ]))]),
                _: 1
              }, 8, ["loading"]),
              _createVNode(_component_el_button, {
                type: "danger",
                disabled: drawer.value.loading,
                onClick: onCancel
              }, {
                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                  _createTextVNode("取消", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"])
            ])
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "close-on-click-modal", "close-on-press-escape"])
  ]))
}
}

})